import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { rupiah, dmy } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Scissors, Save } from "lucide-react";

const FIELDS = [
  ["jumlah_operator", "Jumlah Operator Jahit"], ["upah_operator", "Upah Operator / Hari"], ["uang_makan_operator", "Uang Makan Operator / Hari"],
  ["jumlah_finishing", "Jumlah Finishing"], ["upah_finishing", "Upah Finishing / Hari"], ["gaji_kepala_produksi", "Gaji Kepala Produksi / Bulan"],
  ["hari_kerja", "Hari Kerja / Bulan"], ["target_output", "Target Output / Hari"], ["benang", "Benang / Pcs"], ["rib", "Rib / Pcs"], ["cutting", "Cutting / Pcs"],
];

export default function SewingCost() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [f, setF] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, ""])));
  const [preview, setPreview] = useState(null);
  const [effective, setEffective] = useState(wibToday());
  const load = () => { api.get("/costing/sewing").then((r) => setRows(r.data)); };
  useEffect(load, []);

  useEffect(() => {
    const nums = Object.fromEntries(FIELDS.map(([k]) => [k, Number(f[k] || 0)]));
    if (!nums.hari_kerja || !nums.target_output) { setPreview(null); return; }
    const t = setTimeout(() => {
      api.post("/costing/sewing/preview", { ...nums, effective_from: effective }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(t);
  }, [f, effective]);

  const save = async () => {
    try { await api.post("/costing/sewing", { ...Object.fromEntries(FIELDS.map(([k]) => [k, Number(f[k] || 0)])), effective_from: effective }); toast.success("Konfigurasi biaya jahit aktif tersimpan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Pengaturan Biaya Jahit" subtitle="Metode SPH: Biaya Tetap/Pcs + Biaya Variabel/Pcs. Menyimpan membuat konfigurasi aktif baru (versi lama non-aktif)." testId="sewing-header" />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label]) => (
              <div key={k}><Label className="text-xs text-slate-500">{label}</Label><Input type="number" className="mt-1" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`sw-${k}`} /></div>
            ))}
            <div><Label className="text-xs text-slate-500">Berlaku Sejak</Label><Input type="date" className="mt-1" value={effective} onChange={(e) => setEffective(e.target.value)} /></div>
          </div>
          {can("costing.manage") && <Button onClick={save} className="w-full bg-indigo-600 hover:bg-indigo-500" data-testid="save-sewing"><Save className="mr-1.5 h-4 w-4" />Simpan & Aktifkan</Button>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="sewing-preview">
          <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-400">Hasil Perhitungan</p>
          {!preview ? <p className="text-sm text-slate-400">Lengkapi input untuk melihat biaya jahit / pcs.</p> : (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Biaya Operator / Hari</span><b>{rupiah(preview.operator_day)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Biaya Finishing / Hari</span><b>{rupiah(preview.finishing_day)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Biaya Kepala Produksi / Hari</span><b>{rupiah(preview.kepala_day)}</b></div>
              <div className="flex justify-between border-t pt-1"><span className="text-slate-600">Total Biaya Tetap / Hari</span><b>{rupiah(preview.total_fixed_day)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Biaya Tetap / Pcs</span><b>{rupiah(preview.fixed_per_pcs)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Biaya Variabel / Pcs</span><b>{rupiah(preview.variable_per_pcs)}</b></div>
              <div className="flex justify-between border-t pt-2 text-base"><span className="font-bold">BIAYA JAHIT / PCS</span><b data-testid="sewing-per-pcs">{rupiah(preview.sewing_per_pcs)}</b></div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Berlaku Sejak</TableHead><TableHead className="text-right">Biaya Tetap/Pcs</TableHead><TableHead className="text-right">Variabel/Pcs</TableHead><TableHead className="text-right">Biaya Jahit/Pcs</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((r) => (
              <TableRow key={r.id}><TableCell className="text-xs">{dmy(r.effective_from)}</TableCell><TableCell className="text-right">{rupiah(r.fixed_per_pcs)}</TableCell><TableCell className="text-right">{rupiah(r.variable_per_pcs)}</TableCell><TableCell className="text-right font-medium">{rupiah(r.sewing_per_pcs)}</TableCell><TableCell><StatusBadge status={r.status === "active" ? "Aktif" : "Nonaktif"} /></TableCell></TableRow>
            ))}</TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
